import { useState } from 'react';
import { type Event } from '@/types/event';
import { formatDate, formatTimeRange } from '@/utils/date';
import { clsx } from 'clsx';
import { LazyImage } from '@/components/LazyImage/LazyImage';
import styles from './EventDetails.module.css';

interface EventDetailsProps {
  event: Event;
  isOpen: boolean;
  onShare?: (e: React.MouseEvent) => void;
  hasCopied?: boolean;
}

const CLAMP_THRESHOLD = 400; // chars before the description gets a MORE toggle

function formatPriceRange(event: Event): string | null {
  if (!event.minPrice) return null;
  if (event.maxPrice && event.maxPrice !== event.minPrice) {
    return `${event.minPrice} – ${event.maxPrice}`;
  }
  return `FROM ${event.minPrice}`;
}

/**
 * Expandable event details component
 * Uses CSS Grid for smooth height animation
 */
export function EventDetails({ event, isOpen, onShare, hasCopied }: EventDetailsProps) {
  const [showFullDescription, setShowFullDescription] = useState(false);

  const flyerSrc = event.flyerUrl || event.imageUrl;
  const flyerRatio =
    event.flyerWidth && event.flyerHeight
      ? `${event.flyerWidth} / ${event.flyerHeight}`
      : '2 / 1';
  const description = event.fullDescription || event.description;
  const isClampable = description.length > CLAMP_THRESHOLD;
  const priceRange = formatPriceRange(event);

  return (
    <div className={clsx(styles.detailsWrapper, isOpen && styles.open)}>
      <div className={styles.detailsInner}>
        {flyerSrc && (
          <LazyImage
            src={flyerSrc}
            alt={event.name}
            className={styles.image}
            aspectRatio={flyerRatio}
          />
        )}

        <div className={styles.meta}>
          <p className={styles.datetime}>
            {formatDate(event.start)}
          </p>
          <p className={styles.venue}>{event.venue.name}</p>
          {event.venue.address && event.venue.address !== 'TBA' && (
            <p className={styles.address}>{event.venue.address}</p>
          )}
          <p className={styles.datetime}>
            {formatTimeRange(event.start, event.end)}
          </p>
          {event.isSoldOut ? (
            <p className={styles.soldOut}>SOLD OUT</p>
          ) : (
            priceRange && <p className={styles.price}>{priceRange}</p>
          )}
        </div>

        <p
          className={clsx(
            styles.description,
            isClampable && !showFullDescription && styles.clamped
          )}
        >
          {description}
        </p>
        {isClampable && (
          <button
            className={styles.moreButton}
            onClick={(e) => {
              e.stopPropagation();
              setShowFullDescription((prev) => !prev);
            }}
          >
            {showFullDescription ? 'LESS -' : 'MORE +'}
          </button>
        )}

        <a
          href={event.url}
          target="_blank"
          rel="noopener noreferrer"
          className={clsx(styles.ctaButton, event.isSoldOut && styles.soldOutButton)}
          onClick={(e) => e.stopPropagation()}
        >
          {event.isSoldOut ? 'SOLD OUT' : (event.buttonLabel || 'TICKETS')}
        </a>
        {onShare && (
          <button
            className={clsx(styles.shareButton, hasCopied && styles.copied)}
            onClick={onShare}
            disabled={hasCopied}
          >
            {hasCopied ? 'COPIED' : 'SHARE'}
          </button>
        )}
      </div>
    </div>
  );
}
